// src/components/FeedbackModal.tsx
import { useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import toast from 'react-hot-toast';
import { fetchUserAttributes } from 'aws-amplify/auth';

const client = generateClient<Schema>();

type FeedbackCategory = 'bug' | 'feature' | 'general' | 'ui';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
      void ['🔍 Checking client.models...'];
  void ['Available models:', Object.keys(client.models)];
  void ['Feedback model exists?', 'Feedback' in client.models];
  void ['Feedback model value:', client.models.Feedback];
    void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
    void ['🚀 FEEDBACK SUBMISSION STARTED'];
    void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
    void ['📅 Timestamp:', new Date().toISOString()];
    void ['📝 Form Data:', { category, titleLength: title.length, descLength: description.length }];
    
    // Validation
    if (!title.trim() || !description.trim()) {
      void ['❌ Validation failed: Empty fields'];
      toast.error('Please fill in all fields');
      return;
    }
    void ['✅ Validation passed'];

    setSubmitting(true);

    try {
      // ============================================
      // STEP 1: Get User Info
      // ============================================
      void ['\n📍 STEP 1: Fetching user attributes...'];
      const userAttrs = await fetchUserAttributes();
      void ['✅ User attributes retrieved:', {
        sub: userAttrs.sub,
        email: userAttrs.email,
        hasOtherAttrs: Object.keys(userAttrs).length > 2
      }];
      
      const userId = userAttrs.sub || 'unknown';
      const userEmail = userAttrs.email || '';
      
      if (userId === 'unknown') {
        void ['⚠️ WARNING: userId is "unknown" - user might not be authenticated properly'];
      }

      // ============================================
      // STEP 2: Capture Context
      // ============================================
      void ['\n📍 STEP 2: Capturing context...'];
      const context = {
        currentPage: window.location.pathname,
        userAgent: navigator.userAgent,
        screenWidth: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString(),
      };
      void ['✅ Context captured:', {
        currentPage: context.currentPage,
        screenWidth: context.screenWidth,
        userAgentLength: context.userAgent.length,
        timestamp: context.timestamp
      }];

      // ============================================
      // STEP 3: Prepare Feedback Data
      // ============================================
      void ['\n📍 STEP 3: Preparing feedback data...'];
      const feedbackData = {
        userId,
        userEmail,
        category,
        title: title.trim(),
        description: description.trim(),
        currentPage: context.currentPage,
        userAgent: context.userAgent,
        screenWidth: context.screenWidth,
        timestamp: context.timestamp,
        status: 'new',
      };
      void ['✅ Feedback data prepared:'];
      void ['  - userId:', userId];
      void ['  - userEmail:', userEmail];
      void ['  - category:', category];
      void ['  - title:', title.trim()];
      void ['  - description length:', description.trim().length];
      void ['  - currentPage:', context.currentPage];
      void ['  - screenWidth:', context.screenWidth];
      void ['  - timestamp:', context.timestamp];
      void ['  - status:', 'new'];

      // ============================================
      // STEP 4: Submit to API
      // ============================================
      void ['\n📍 STEP 4: Calling API...'];
      void ['🔄 Executing: client.models.Feedback.create()'];
      void ['📦 Full payload:', JSON.stringify(feedbackData, null, 2)];
      
      const startTime = performance.now();
      const result = await client.models.Feedback.create(feedbackData);
      const endTime = performance.now();
      
      void [`✅ API call completed in ${(endTime - startTime).toFixed(2)}ms`];

      // ============================================
      // STEP 5: Process Response
      // ============================================
      void ['\n📍 STEP 5: Processing response...'];
      void ['📦 Raw result object:', result];
      void ['📦 result.data:', result.data];
      void ['📦 result.errors:', result.errors];
      
      // Check for GraphQL errors
      if (result.errors && result.errors.length > 0) {
        void ['❌ GraphQL Errors detected:'];
        result.errors.forEach((error, index) => {
          void [`  Error ${index + 1}:`, {
            message: error.message,
            path: error.path,
            locations: error.locations,
            extensions: error.extensions
          }];
        });
        toast.error(`Failed: ${result.errors[0].message}`);
        void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
        void ['❌ FEEDBACK SUBMISSION FAILED (GraphQL Errors)'];
        void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n'];
        return;
      }

      // Check for missing data
      if (!result.data) {
        void ['❌ No data returned from API'];
        void ['   This usually means:'];
        void ['   1. Authorization failed (check Cognito groups)'];
        void ['   2. DynamoDB write failed'];
        void ['   3. AppSync resolver issue'];
        toast.error('Failed to submit feedback - no data returned');
        void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
        void ['❌ FEEDBACK SUBMISSION FAILED (No Data)'];
        void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n'];
        return;
      }

      // ============================================
      // SUCCESS!
      // ============================================
      void ['✅ Data received from API:', result.data];
      void ['✅ Feedback ID:', result.data.id];
      
      toast.success('Feedback submitted! Thank you! 🙏', {
        duration: 4000,
        icon: '✨',
      });

      // Reset and close
      void ['🧹 Resetting form and closing modal...'];
      setTitle('');
      setDescription('');
      setCategory('bug');
      onClose();
      
      void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
      void ['✅ FEEDBACK SUBMISSION SUCCESSFUL'];
      void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n'];
      
} catch (error) {
      // ============================================
      // ERROR HANDLING
      // ============================================
      void ['\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
      void ['❌ EXCEPTION CAUGHT'];
      void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
      
      // Type guard for error
      const err = error as any;
      
      void ['🔴 Error Type:', err?.constructor?.name || 'Unknown'];
      void ['🔴 Error Message:', err?.message || 'No message'];
      
      // Log full error object
      void ['🔴 Full Error Object:'];
      try {
        void [JSON.stringify(error, Object.getOwnPropertyNames(error), 2)];
      } catch {
        void [error];
      }
      
      // Log error properties safely
      if (err && typeof err === 'object') {
        void ['🔴 Error Properties:'];
        try {
          const keys = Object.keys(err);
          keys.forEach(key => {
            void [`  - ${key}:`, err[key]];
          });
        } catch (e) {
          void ['  Could not enumerate error properties'];
        }
      }
      
      // Check for common error types
      const errorMessage = String(err?.message || '');
      if (errorMessage.toLowerCase().includes('not authorized')) {
        void ['💡 HINT: Authorization error - check:'];
        void ['   1. User is authenticated (logged in)'];
        void ['   2. Cognito user has correct permissions'];
        void ['   3. Schema authorization rules allow .create()'];
      } else if (errorMessage.toLowerCase().includes('network')) {
        void ['💡 HINT: Network error - check:'];
        void ['   1. Internet connection'];
        void ['   2. AppSync endpoint is accessible'];
        void ['   3. CORS settings in backend'];
      } else if (errorMessage.toLowerCase().includes('required')) {
        void ['💡 HINT: Missing required field - check:'];
        void ['   1. All .required() fields have values'];
        void ['   2. Field names match schema exactly'];
      }
      
      toast.error('Failed to submit feedback. Please try again.');
      
      void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━'];
      void ['❌ FEEDBACK SUBMISSION FAILED (Exception)'];
      void ['━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n'];
      
    } finally {
      setSubmitting(false);
      void ['🏁 Finally block executed - submitting set to false\n'];
    }
  };

  const categories = [
    { id: 'bug' as FeedbackCategory, icon: '🐛', label: 'Bug Report', desc: 'Something isn\'t working' },
    { id: 'feature' as FeedbackCategory, icon: '💡', label: 'Feature Request', desc: 'Suggest an improvement' },
    { id: 'ui' as FeedbackCategory, icon: '🎨', label: 'UI/UX Issue', desc: 'Design or usability' },
    { id: 'general' as FeedbackCategory, icon: '💬', label: 'General Feedback', desc: 'Other thoughts' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand to-branddark text-white px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                
                <span>Feedback</span>
              </h2>
              <p className="text-sm text-white/90 mt-1">Help us make Crushhh better!</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              What type of feedback?
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    category === cat.id
                      ? 'border-brand bg-brand/5 shadow-md'
                      : 'border-gray-200 hover:border-brand/50 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{cat.icon}</span>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900">{cat.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{cat.desc}</div>
                    </div>
                    {category === cat.id && (
                      <svg className="w-5 h-5 text-brand" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your feedback"
              maxLength={100}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand transition-colors"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {title.length}/100 characters
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Details <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide as much detail as possible..."
              rows={6}
              maxLength={1000}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand transition-colors resize-none"
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {description.length}/1000 characters
            </div>
          </div>

          {/* Context Info */}
          <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600">
            <div className="font-semibold mb-2">📍 Auto-captured info:</div>
            <div className="space-y-1">
              <div>• Current page: {window.location.pathname}</div>
              <div>• Screen size: {window.innerWidth}x{window.innerHeight}</div>
              <div>• Browser: {navigator.userAgent.split(' ').slice(-2).join(' ')}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100 transition-colors"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !description.trim()}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-brand to-branddark text-white font-bold rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}